import React from 'react';

import { Logger } from 'services/logger';
import { ONETRUST_CATEGORIES, OneTrustService } from 'services/oneTrust';

const sdkContent = require('!raw-loader!@contentstack/personalize-edge-sdk/dist/personalize-edge-sdk.min').default;

export const PERSONALIZE_TIMEOUT_CLASS = 'personalize-load-timeout';
const PERSONALIZE_TIMEOUT = 5000;

export class PersonalizeSDKError extends Error {
  constructor(message) {
    super(message);
    this.name = 'PersonalizeSDKError';
  }
}

export class PersonalizeSDKService {
  static getPersonalizeInitializationScript() {
    return (
      <script
        key="personalize-init-script"
        dangerouslySetInnerHTML={{
          __html: `
      ${sdkContent}

      const initPersonalizeSDK = async () => {
        if (window.personalizeSDK) {
          return window.personalizeSDK;
        }

        window.personalizeSDK = (async () => {
          const sdk = await personalize.init('${process.env.CONTENTSTACK_PERSONALIZE_UID}');
          return sdk;
        })();

        return window.personalizeSDK;
      };

      initPersonalizeSDK();
    `,
        }}
      />
    );
  }

  static getPersonalizeTimeoutScript() {
    return (
      <script
        key="personalize-timeout-script"
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
      window.personalizeTimer = setTimeout(() => {
        window.personalizeLoadTimeout = true;
        document.body.classList.add('${PERSONALIZE_TIMEOUT_CLASS}');
      }, ${PERSONALIZE_TIMEOUT});
      `,
        }}
      />
    );
  }

  /*
   * Personalize SDK is loaded in html <head> to give it the highest priority.
   * Communication happens through global objects.
   */
  static async waitForSdk() {
    if (!window.personalizeSDK) {
      throw new PersonalizeSDKError('Personalize SDK not initialized.');
    }
    const sdk = await window.personalizeSDK;

    if (PersonalizeSDKService.hasTimeout()) {
      throw new PersonalizeSDKError('Personalize SDK load timeout exceeded.');
    }

    if (!sdk || typeof sdk !== 'object') {
      throw new PersonalizeSDKError('Personalize SDK is not available.');
    }

    return sdk;
  }

  /**
   * Check if timeout for loading personalized content has been reached
   * @returns {boolean}
   */
  static hasTimeout() {
    return !!window.personalizeLoadTimeout;
  }

  /**
   * Clears global timer that loads Personalize SDK only within a certain time frame.
   */
  static clearTimer() {
    clearTimeout(window.personalizeTimer);
  }

  static async setAttribute(attribute, value) {
    try {
      const targetingConsent = await OneTrustService.checkConsent(ONETRUST_CATEGORIES.targeting);
      if (!targetingConsent) {
        Logger.warn('Targeting consent not granted, skipping Personalize attribute setting.');
        return;
      }

      const personalize = await PersonalizeSDKService.waitForSdk();
      if (!personalize) return;
      await personalize.set({
        [attribute]: value,
      });
    } catch (error) {
      Logger.warn('Error setting visitor status in Personalize SDK:', error);
    }
  }
}
